import React from "react";
import { Helmet } from "react-helmet-async";
import Grid, { type Actions, type Columns } from "../../component/Grid";
import { useCrumbs } from "../../component/breadcrumbs";
import { useNavigate, useSearchParams } from "react-router-dom";
import useLocale from "../../util/i18n";
import { dismissPurchaseOrder, getPurchaseOrder, getPurchaseOrderStatuses, getSupplier, savePurchaseOrder, type PurchaseOrder, type PurchaseOrderItem } from "../../api/erp";
import { getConfig } from "../../api/core";
import { type OptionType } from "../../component/select";
import { format } from "date-fns";
import { MDBBtn, MDBInput, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter } from "mdb-react-ui-kit";
import ExcelJS from "exceljs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Toast from "../../component/toast";

export default function PurchaseOrders() {
    let { 0: editable, 1: setEditable } = React.useState(false);
    let { 0: query, 1: setQuery } = useSearchParams();
    let { 0: statuses, 1: setStatuses } = React.useState<Array<OptionType>>([]);
    let { 0: suppliers, 1: setSuppliers } = React.useState<Array<OptionType>>([]);
    let { 0: setCrumbs } = useCrumbs();
    let { 0: __, 3: locale } = useLocale();
    let { 0: loading, 1: setLoading } = React.useState(false);
    let navigate = useNavigate();
    let columns = React.useMemo<Columns>(() => ({
        order_id: {
            label: 'ID',
            filterable: 'filter[order_id]'
        },
        supplier: {
            label: __('Supplier'),
            filterable: 'filter[supplier]',
            type: 'select',
            options: suppliers,
            format: value => suppliers.find(s => s.value === value)?.label ?? ''
        },
        ordered_at: {
            label: __('Ordered at'),
            format: value => parseInt(value) ? format(value * 1000, 'PPPP', { locale }) : '-'
        },
        delivered_at: {
            label: __('Delivered at'),
            format: value => parseInt(value) ? format(value * 1000, 'PPPP', { locale }) : '-'
        },
        received_at: {
            label: __('Received at'),
            format: value => parseInt(value) ? format(value * 1000, 'PPPP', { locale }) : '-'
        },
        status: {
            label: __('Status'),
            className: 'w-1 text-center text-nowrap',
            filterable: 'filter[status]',
            type: 'select',
            options: statuses
        }
    }), [__, suppliers, statuses]);
    let actions = React.useMemo<Actions>(() => ([
        {
            label: __(editable ? 'Edit' : 'View'),
            onClick: (item: PurchaseOrder) => {
                window.sessionStorage.setItem('PO-' + item.id, JSON.stringify(item));
                navigate('/purchase-order/' + (editable ? 'edit' : 'view') + '?id=' + item.id);
            }
        },
        {
            label: __('Excluded in Statistics'),
            condition: item => item.dismiss != 1,
            onClick: (item, ref) => {
                dismissPurchaseOrder({ id: item.id, dismiss: 1 }).then(() => {
                    ref.redraw({ ...item, dismiss: 1 });
                });
            }
        },
        {
            label: __('Included in Statistics'),
            condition: item => item.dismiss == 1,
            onClick: (item, ref) => {
                dismissPurchaseOrder({ id: item.id, dismiss: 0 }).then(() => {
                    ref.redraw({ ...item, dismiss: 0 });
                });
            }
        }
    ]), [__, editable]);
    const doLoad = React.useCallback((params: Record<string, any>) => {
        if (!params.sort) {
            params.sort = 'ordered_at DESC';
        }
        return getPurchaseOrder(params);
    }, []);
    const upload = React.useRef<(file: File) => Promise<boolean>>(null);
    let onDropFile = React.useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let files = e.dataTransfer.files;
        if (files.length === 0) {
            return;
        }
        setLoading(true);
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            file && (await upload.current?.(file));
        }
        setLoading(false);
        document.location.reload();
    }, [setLoading]);
    React.useEffect(() => {
        setCrumbs(['Purchase Order']);
        if (window.sessionStorage.getItem('erp-editable')) {
            setEditable(window.sessionStorage.getItem('erp-editable') === '1');
        } else {
            getConfig('erp/service').then(response => {
                let result = !Boolean(response['erp/service']) ? '1' : '0';
                window.sessionStorage.setItem('erp-editable', result);
                setEditable(result === '1');
            });
        }
        let statuses = window.sessionStorage.getItem('PO-status');
        if (statuses) {
            setStatuses(JSON.parse(statuses));
        } else {
            getPurchaseOrderStatuses().then(response => {
                let result = response.map(item => ({ value: item.status, label: item.status }));
                window.sessionStorage.setItem('PO-status', JSON.stringify(result));
                setStatuses(result);
            });
        }
        let suppliers = window.sessionStorage.getItem('suppliers');
        if (suppliers) {
            setSuppliers(JSON.parse(suppliers));
        } else {
            getSupplier({ limit: -1 }).then(response => {
                if (typeof response !== 'string') {
                    let result = response.data.map(item => ({ value: item.id, label: item.name }));
                    window.sessionStorage.setItem('suppliers', JSON.stringify(result));
                    setSuppliers(result);
                }
            });
        }
    }, [setEditable, setStatuses]);
    return (
        <>
            <Helmet>
                <title>{__('Purchase Order')}</title>
            </Helmet>
            <Grid newUrl={editable ? '/purchase-order/edit' : undefined} onDrop={editable ? onDropFile : undefined} columns={columns} actions={actions} query={query} setQuery={setQuery} doLoad={doLoad} />
            {editable ? (
                <>
                    <Import ref={upload} />
                    {loading ? (
                        <div className="loading-modal">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                        </div>
                    ) : null}
                </>
            ) : null}
        </>
    );
}

const DATES: Record<number, 'ordered_at' | 'delivered_at' | 'received_at'> = {
    3: 'ordered_at',
    4: 'delivered_at',
    5: 'received_at'
};

const Import = React.forwardRef<(file: File) => Promise<boolean>>(function (_, ref) {
    let { 0: show, 1: setShow } = React.useState(false);
    let { 0: loading, 1: setLoading } = React.useState(false);
    let { 0: __ } = useLocale();
    let template = React.useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet();
        sheet.getCell(1, 1).value = __('Order ID');
        sheet.getCell(2, 1).value = __('Supplier');
        sheet.getCell(3, 1).value = __('Ordered at');
        sheet.getCell(4, 1).value = __('Delivered at');
        sheet.getCell(5, 1).value = __('Received at');
        sheet.getCell(6, 1).value = __('Status');
        sheet.getCell(7, 1).value = 'SKU';
        sheet.getCell(7, 2).value = __('Ordered Qty');
        sheet.getCell(7, 3).value = __('Receipt Qty');
        sheet.getCell(1, 1).style.font = sheet.getCell(2, 1).style.font = sheet.getCell(3, 1).style.font = sheet.getCell(4, 1).style.font = sheet.getCell(5, 1).style.font =
            sheet.getCell(6, 1).style.font = sheet.getCell(7, 1).style.font = sheet.getCell(7, 2).style.font = sheet.getCell(7, 3).style.font = { bold: true };
        workbook.xlsx.writeBuffer().then(buffer => {
            let a = document.createElement('a');
            a.download = 'template-PO.xlsx';
            a.href = URL.createObjectURL(new Blob([buffer]));
            document.body.append(a);
            a.click();
            URL.revokeObjectURL(a.href);
            a.remove();
        });
    }, [__]);
    let doSubmit = React.useCallback(async (file: File) => {
        try {
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(await file.arrayBuffer());
            let params: Array<Partial<PurchaseOrder> & { item: Array<PurchaseOrderItem> }> = [];
            workbook.eachSheet(sheet => {
                let order: Partial<PurchaseOrder> & { item: Array<PurchaseOrderItem> } = {
                    order_id: sheet.getCell(1, 2).text,
                    supplier: sheet.getCell(2, 2).text,
                    ordered_at: 0,
                    delivered_at: 0,
                    received_at: 0,
                    status: sheet.getCell(6, 2).text,
                    item: []
                };
                if (!order.order_id) {
                    return;
                }
                for (let r in DATES) {
                    let value = sheet.getCell(parseInt(r), 2).value;
                    if (value && value instanceof Date) {
                        order[DATES[r] as 'ordered_at'] = Math.round(value.getTime() / 1000)
                    }
                }
                for (let r = 8; ; r++) {
                    let row = sheet.getRow(r);
                    let item: PurchaseOrderItem = {
                        id: '',
                        order_id: order.order_id,
                        delivered_at: order.delivered_at!,
                        ordered_at: order.ordered_at!,
                        sku: row.getCell(1).text.trim(),
                        ordered_qty: parseFloat(row.getCell(2).text) || 0,
                        receipt_qty: parseFloat(row.getCell(3).text) || 0
                    }
                    if (!item.sku) {
                        break;
                    }
                    if (item.ordered_qty > 0) {
                        order.item.push(item);
                    }
                }
                params.push(order);
            });
            await savePurchaseOrder(params);
            return true;
        } catch {
            return false;
        }
    }, [])
    let onSubmit = React.useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let file = (new FormData(e.target as HTMLFormElement)).get('file') as File | null;
        if (file) {
            setLoading(true);
            if (await doSubmit(file)) {
                setShow(false);
                Toast.show(__('Data has been saved.')).then(() => {
                    window.location.reload();
                });
            }
            setLoading(false);
        }
    }, [setLoading, setShow]);
    React.useEffect(() => {
        let l = setShow.bind(null, true);
        document.addEventListener('import', l);
        return () => {
            document.removeEventListener('import', l);
        };
    }, [setShow]);
    React.useImperativeHandle(ref, () => doSubmit, [doSubmit]);
    return (
        <>
            <div data-importable="1" />
            <MDBModal open={show} onClose={setShow.bind(null, false)} tabIndex='-1'>
                <MDBModalDialog centered>
                    <MDBModalContent tag="form" onSubmit={onSubmit}>
                        <MDBModalBody>
                            {loading ? (
                                <div className="d-flex justify-content-center">
                                    <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                                </div>
                            ) : (
                                <MDBInput type="file" name="file" label={__('File')} className="active" required accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" />
                            )}
                        </MDBModalBody>
                        <MDBModalFooter>
                            <MDBBtn type="button" color="info" onClick={template}>{__('Template')}</MDBBtn>
                            <MDBBtn disabled={loading} type="button" color="secondary" onClick={setShow.bind(null, false)}>{__('Cancel')}</MDBBtn>
                            <MDBBtn disabled={loading} type="submit">{__('Import')}</MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </>
    );
});
